/* eslint-disable react-refresh/only-export-components -- stage hook + its menu button live together */
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import {
  PositionStage,
  type ClosingDecision,
  type PositionStage as PositionStageCode,
  type SetPositionStage,
} from '@clawhire/shared';
import { api, isApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/toaster';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TONE_CLASSES, TONE_DOT_CLASSES, toneFor } from '@/components/data/tones';
import { ClosingDialog } from './ClosingDialog';
import { useInvalidatePositions } from './queries';
import type { StageResponse } from './types';

interface PendingClosing {
  positionId: string;
  stage: PositionStageCode;
  decisions?: ClosingDecision[];
}

export interface UseStageChangeResult {
  /**
   * Move a position to a stage. Lost/Dropped (and a 409 `DECISIONS_REQUIRED`) open the closing
   * dialog instead and resolve with `null`, so callers can roll back an optimistic update. Other
   * API errors are toasted and rethrown.
   */
  change: (positionId: string, stage: PositionStageCode) => Promise<StageResponse | null>;
  openClosing: (positionId: string, stage: PositionStageCode) => void;
  /** Render this once near the caller. */
  dialog: React.ReactNode;
  isPending: boolean;
}

/** Stage transitions with the §6.1 closing dialog, shared by the board and the detail header. */
export function useStageChange(
  options: { onChanged?: (result: StageResponse) => void } = {},
): UseStageChangeResult {
  const { onChanged } = options;
  const invalidate = useInvalidatePositions();
  const [closing, setClosing] = React.useState<PendingClosing | null>(null);

  const mutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: SetPositionStage }) =>
      api.post<StageResponse>(`/positions/${encodeURIComponent(id)}/stage`, body),
  });

  const change = React.useCallback(
    async (positionId: string, stage: PositionStageCode): Promise<StageResponse | null> => {
      if (stage === 'LOST' || stage === 'DROPPED') {
        setClosing({ positionId, stage });
        return null;
      }
      try {
        const result = await mutation.mutateAsync({ id: positionId, body: { stage } });
        toast.success(`Stage → ${PositionStage.label(stage)}`);
        await invalidate();
        onChanged?.(result);
        return result;
      } catch (e) {
        if (isApiError(e) && e.code === 'DECISIONS_REQUIRED') {
          const details = e.details as { decisions?: ClosingDecision[] } | undefined;
          setClosing({ positionId, stage, decisions: details?.decisions });
          return null;
        }
        toast.error(e instanceof Error ? e.message : 'Could not change the stage');
        throw e;
      }
    },
    [mutation, invalidate, onChanged],
  );

  const openClosing = React.useCallback((positionId: string, stage: PositionStageCode) => {
    setClosing({ positionId, stage });
  }, []);

  const dialog = (
    <ClosingDialog
      open={closing !== null}
      onOpenChange={(open) => {
        if (!open) setClosing(null);
      }}
      positionId={closing?.positionId ?? null}
      stage={closing?.stage ?? null}
      decisions={closing?.decisions}
      onDone={(result) => {
        setClosing(null);
        onChanged?.(result);
      }}
    />
  );

  return { change, openClosing, dialog, isPending: mutation.isPending };
}

export interface StageMenuProps {
  stage: PositionStageCode;
  onSelect: (stage: PositionStageCode) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

/** The stage badge as a menu button: pick another stage from the list. */
export function StageMenu({ stage, onSelect, disabled = false, size = 'md' }: StageMenuProps) {
  const tone = toneFor(PositionStage, stage);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          aria-label={`Stage: ${PositionStage.label(stage)}. Change stage`}
          className={cn(
            'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border font-medium transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default',
            size === 'sm' ? 'px-1.5 py-0 text-[11px] leading-5' : 'px-2 py-0.5 text-xs leading-5',
            TONE_CLASSES[tone],
            !disabled && 'hover:opacity-80',
          )}
        >
          <span className={cn('h-1.5 w-1.5 rounded-full', TONE_DOT_CLASSES[tone])} aria-hidden="true" />
          {PositionStage.label(stage)}
          {disabled ? null : <ChevronDown className="h-3 w-3" aria-hidden="true" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuLabel>Move to</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {PositionStage.codes.map((code) => (
          <DropdownMenuItem key={code} disabled={code === stage} onSelect={() => onSelect(code)}>
            <span
              className={cn('h-2 w-2 rounded-full', TONE_DOT_CLASSES[toneFor(PositionStage, code)])}
              aria-hidden="true"
            />
            {PositionStage.label(code)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
